/**
 * @kkp-fraud/shared — the contracts every package codes against.
 *
 * Sources of truth:
 *   - KKP PreDetect JSON (18 Sep 2026) — 31 request fields, all strings on the wire
 *   - Microsoft response proposal (TFM Feeder API spec §D/§E, OQ-07 — not yet confirmed by KKP)
 *   - rules/rules.v1.json — the rule document shape
 *
 * Nothing in here does I/O. Keep it dependency-free.
 */
export const KKP_FIELDS = [
    'reference_card_no', 'transaction_datetime', 'card_acceptor_term_id', 'card_acceptor_id', 'card_acceptor_name',
    'card_acceptor_country_code', 'customer_no', 'pos_entry_mode', 'processing_code', 'transaction_id', 'message_type',
    'transaction_amount', 'cardholder_billing_amount', 'local_transaction_time', 'local_transaction_date', 'merchant_type',
    'acquiring_inst_country_code', 'pos_term_pan_entry_mode', 'pos_term_pin_entry_mode', 'acceptor_name', 'acceptor_city',
    'electronic_commerce', 'fraud_score', 'fraud_score_reason_code', 'currency_code', 'pos_term_attendence', 'pos_tran_status',
    'cardholder_act_term_level', 'pos_card_indicator', 'switching_name', 'card_product_name',
    // Added 23 Sep 2026 (KKP "Fields for Pre-Detection.xlsx", row 28). Appended rather than
    // inserted at its sheet position so no existing index into this list moves.
    'additional_pos_info',
];
export const RESPONSE_CODE = {
    APPROVE: '00',
    APPROVE_NOTIFY: '00',
    STEP_UP: '65', // "exceeds withdrawal frequency" is the conventional soft-decline hint; host owns the final mapping (DEP-01)
    DECLINE: '59', // suspected fraud
};
/** The two ops whose value is a Master list id rather than a literal. */
export const SET_OPS = ['in_set', 'not_in_set'];
export function isSetOp(op) {
    return op === 'in_set' || op === 'not_in_set';
}
/** `custom_field_name` — what the engine accepts as a custom field's name. */
export const CUSTOM_FIELD_NAME_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;
// ---- 2c rule sets: the registry ---------------------------------------------
// Container `rules`, partition `_registry`, id `registry` (rust/fraud-types/src/rulesets.rs).
// The admin service writes it; the engine reads it. Missing = one set, the primary, enabled.
/** `^[a-z][a-z0-9-]{1,39}$` — a set id, and the partition its documents live in. */
export const RULE_SET_ID_PATTERN = /^[a-z][a-z0-9-]{1,39}$/;
export const RULE_SET_REGISTRY_ID = 'registry';
export const RULE_SET_REGISTRY_PARTITION = '_registry';
// =============================================================================
// 6. Event contracts — Kafka / Event Hubs topics
// =============================================================================
export const TOPICS = {
    cardTransactions: 'card-transactions', // KKP Kafka → (bridge) → Event Hubs. Payload: CardTransactionEvent
    authDecisions: 'auth-decisions', // decision-api → Event Hubs after responding. Payload: DecisionEvent
    appEvents: 'app-events', // Dime! app security events (device bind, PIN reset). Payload: AppEvent
};
// ---- decisions by period ------------------------------------------------------
/** Most rows one `GET /admin/decisions` page may carry. */
export const ADMIN_DECISIONS_MAX_LIMIT = 5000;
/**
 * Routes (decision-api):
 *   GET  /healthz                      liveness
 *   GET  /readyz                       readiness — state store + rule set loaded
 *   POST /v1/predetect                 the decision  (Authorization: <API_KEY>; x-request-id echoed)
 *   GET  /admin/rules                  AdminRulesGetResponse
 *   PUT  /admin/rules                  AdminRulesPutRequest → AdminRulesPutResponse   (hot-reloads the engine)
 *   POST /admin/rules/validate         AdminValidateRequest → RuleValidation
 *   POST /admin/rules/dry-run          AdminDryRunRequest → AdminDryRunResponse        (never writes state or audit)
 *   GET  /admin/decisions?limit=100    DecisionAudit[]  (latest first)
 *   GET  /admin/decisions?from=&to=&limit=&cursor=  AdminDecisionsPageResponse  (range + paging; any of the
 *                                      three range params switches to this shape)
 *   GET  /admin/stats                  AdminStatsResponse
 *   GET  /admin/rules/versions                    AdminRuleVersionsResponse      (newest first)
 *   GET  /admin/rules/versions/:version           AdminRuleVersionGetResponse    (the whole document)
 *   POST /admin/rules/versions/:version/activate  AdminActivateVersionRequest → AdminActivateVersionResponse
 *
 * Every /admin/* route requires the header  x-admin-password: <ADMIN_PASSWORD>  (503 when the
 * server has no ADMIN_PASSWORD configured). The feeder's API key does NOT open admin routes.
 */
export const ROUTES = {
    healthz: '/healthz',
    readyz: '/readyz',
    predetect: '/v1/predetect',
    adminRules: '/admin/rules',
    adminValidate: '/admin/rules/validate',
    adminDryRun: '/admin/rules/dry-run',
    adminDecisions: '/admin/decisions',
    adminStats: '/admin/stats',
    adminRuleVersions: '/admin/rules/versions',
};
/** The header every /admin/* call must carry (Feature 2 — the rule-admin login gate). */
export const ADMIN_PASSWORD_HEADER = 'x-admin-password';
/** `/admin/rules/versions/1.0.2-poc` — one place that knows the encoding, for API and UI. */
export function adminRuleVersionPath(version) {
    return `${ROUTES.adminRuleVersions}/${encodeURIComponent(version)}`;
}
/** `/admin/rules/versions/1.0.2-poc/activate`. */
export function adminRuleVersionActivatePath(version) {
    return `${adminRuleVersionPath(version)}/activate`;
}
// =============================================================================
// 8. Small helpers shared by more than one package
// =============================================================================
export const ISO_COUNTRY_TH = 764;
export const ISO_CURRENCY_THB = 764;
/**
 * Bump the patch component of a semver-ish version: '1.0.0-poc' → '1.0.1-poc'.
 * §6.5 — every release is a new version. It lives here because the API decides the next
 * version and the rule-admin UI has to NAME it before the user presses the button.
 */
export function bumpPatch(version) {
    const m = /^(\d+)\.(\d+)\.(\d+)(.*)$/.exec(version.trim());
    if (!m)
        return `${version}.1`;
    return `${m[1]}.${m[2]}.${Number(m[3]) + 1}${m[4] ?? ''}`;
}
export function isMastercardNetwork(switchingName) {
    if (!switchingName)
        return 'UNKNOWN';
    const s = switchingName.toUpperCase();
    if (s.includes('MASTERCARD') || s.includes('MC'))
        return 'MASTERCARD';
    if (s.includes('ITMX'))
        return 'ITMX';
    return 'UNKNOWN';
}
/** A list's id: `MERCHANT_WATCHLIST`. Upper case, digits and `_`, 2–40 characters, a letter first. Immutable. */
export const MASTER_ID_PATTERN = /^[A-Z][A-Z0-9_]{1,39}$/;
export const MASTER_MAX_ITEMS = 200_000;
export const MASTER_MAX_VALUE_LENGTH = 128;
export const MASTER_MAX_IMPORT_BYTES = 10 * 1024 * 1024;
/** One page of `GET /api/masters/{id}/items`: `limit` is clamped to 1..500, default 100. */
export const MASTER_MAX_PAGE = 500;
export const MASTER_DEFAULT_PAGE = 100;
/** A column key: `reason`, `merchant_name`. Lower case, digits and `_`, 1–40 characters, a letter first. */
export const MASTER_COLUMN_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const MASTER_TEMPLATE_MAX_COLUMNS = 20;
/** A note is trimmed; blank means none. Longer than this is refused. */
export const MASTER_MAX_NOTE_LENGTH = 256;
/**
 * The one value rule, shared by the console (before it sends) and mirrored by the admin
 * service (which decides): trimmed, non-empty, at most 128 characters, no control characters;
 * a number list takes any finite number and stores it as `String(Number(raw))` — the same
 * canonical text JavaScript prints, so `"6011.0"`, `" 6011 "` and `"6.011e3"` are one value.
 */
export function canonicalMasterValue(valueType, raw) {
    const s = raw.trim();
    if (s === '')
        return { ok: false, reason: 'empty' };
    if (/[\u0000-\u001f\u007f]/.test(s))
        return { ok: false, reason: 'control_character' };
    if (s.length > MASTER_MAX_VALUE_LENGTH)
        return { ok: false, reason: 'too_long' };
    if (valueType === 'number') {
        // Number('0x10') and Number('') are numbers in JavaScript; neither is a number here.
        if (!/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(s))
            return { ok: false, reason: 'not_a_number' };
        const n = Number(s);
        if (!Number.isFinite(n))
            return { ok: false, reason: 'not_a_number' };
        return { ok: true, value: String(n === 0 ? 0 : n) };
    }
    return { ok: true, value: s };
}
export const WEBHOOK_ID_PATTERN = /^[a-z][a-z0-9-]{1,39}$/;
/** The alert channel kinds that are delivered — and the only ones the console offers. */
export const OFFERED_ALERT_CHANNEL_KINDS = ['email', 'webhook'];
export const WEBHOOK_METHODS = ['GET', 'POST', 'PUT', 'PATCH'];
export const WEBHOOK_MAX_TIMEOUT_MS = 10_000;
/** `retry.attempts` is the TOTAL number of tries, the first one included: 1 = never retry. */
export const WEBHOOK_MAX_ATTEMPTS = 5;
/** Delivery log documents expire after 30 days (per-item `ttl`; the container needs `defaultTtl` on). */
export const WEBHOOK_DELIVERY_TTL_SECONDS = 30 * 24 * 60 * 60;
/** Request body / response body previews in the delivery log are cut at this many bytes. */
export const WEBHOOK_PREVIEW_BYTES = 2048;
/**
 * The body template's variables — the whole set. An unknown placeholder is a validation error
 * on save (admin API) and renders as empty text at send (dispatcher).
 *   decision, transactionId, customerId, amountThb, eventTimeUtc — from the decision
 *   cardId      the card reference masked as the console shows it (`…7001`)
 *   cardIdRaw   the card reference as stored (opt-in: only when the template names it)
 *   merchantId  `txn.merchant_id` (empty when the audit record has no `txn`)
 *   ruleSetId   the rule set the alert rule belongs to
 *   ruleIds     JSON array of the rule ids that fired · hits: JSON array of `{rule_id,name,action,detail}`
 *   alertRuleId the alert rule that fired · consoleUrl: the console's base URL (may be empty)
 * With a JSON content type a string value is inserted JSON-escaped WITHOUT quotes (write
 * `"{{transactionId}}"`), numbers bare, `ruleIds`/`hits` as JSON, a null value as nothing.
 */
export const WEBHOOK_TEMPLATE_VARIABLES = [
    'decision',
    'transactionId',
    'cardId',
    'cardIdRaw',
    'customerId',
    'merchantId',
    'amountThb',
    'eventTimeUtc',
    'ruleSetId',
    'ruleIds',
    'hits',
    'alertRuleId',
    'consoleUrl',
];
export const WEBHOOK_INTERNAL_ROUTE_PREFIX = '/internal/webhooks/';
// end R3 webhooks ==============================================================
// =============================================================================
// Wave 4 API clients — one key per caller of /v1/* (docs/api-clients.md)
// =============================================================================
// The admin service (dotnet/admin-api/ApiClients) writes one `ApiClientDocument` per caller to
// the Cosmos container `api-clients` (pk /id); the Rust decision API reads them
// (`API_CLIENTS_SOURCE`, rust/fraud-types/src/api_clients.rs). The key itself is never stored.
/** Every generated key starts with this; 43 base64url characters (32 random bytes) follow. */
export const API_KEY_PREFIX = 'kkpf_';
/** The client id the decision API gives the single legacy `API_KEY`. Not available to a client. */
export const LEGACY_CLIENT_ID = 'legacy';
/** A client id: a lower-case letter, then 1–39 lower-case letters, digits or `-`. */
export const API_CLIENT_ID_PATTERN = /^[a-z][a-z0-9-]{1,39}$/;
// end Wave 4 API clients =======================================================
//# sourceMappingURL=index.js.map